import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Archive, Search, Upload, Download, Filter, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [overwriteDuplicates, setOverwriteDuplicates] = useState(false);
  const [priceIncludesVat, setPriceIncludesVat] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category_id: '',
    stock_qty: '100',
    low_stock_threshold: '10'
  });
  const [isAddon, setIsAddon] = useState(false);
  const [addonForProducts, setAddonForProducts] = useState([]);
  const [addonForCategories, setAddonForCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    fetchData();
  }, [showArchived, categoryFilter]);

  const fetchData = async () => {
    try {
      let url = `${API}/products?include_archived=${showArchived}`;
      if (categoryFilter && categoryFilter !== 'all') {
        url += `&category_id=${categoryFilter}`;
      }
      const [prodRes, catRes, allProdRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/categories`),
        axios.get(`${API}/products?include_archived=false`)
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setAllProducts(allProdRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load products');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Unknown';
  };

  // Group products by category
  const groupedProducts = products.reduce((acc, product) => {
    if (!searchTerm || product.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      const catName = getCategoryName(product.category_id);
      if (!acc[catName]) acc[catName] = [];
      acc[catName].push(product);
    }
    return acc;
  }, {});

  // Sort category groups by category sort order
  const sortedCategories = Object.keys(groupedProducts).sort((a, b) => {
    const catA = categories.find(c => c.name === a);
    const catB = categories.find(c => c.name === b);
    return (catA?.sort_order || 99) - (catB?.sort_order || 99);
  });

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        price: product.price.toString(),
        category_id: product.category_id,
        stock_qty: product.stock_qty.toString(),
        low_stock_threshold: product.low_stock_threshold.toString()
      });
      setPriceIncludesVat(false);
      setIsAddon(product.is_addon || false);
      setAddonForProducts(product.addon_for_products || []);
      setAddonForCategories(product.addon_for_categories || []);
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        price: '',
        category_id: categories[0]?.id || '',
        stock_qty: '100',
        low_stock_threshold: '10'
      });
      setPriceIncludesVat(false);
      setIsAddon(false);
      setAddonForProducts([]);
      setAddonForCategories([]);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e, keepOpen = false) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      price: parseFloat(formData.price),
      price_includes_vat: priceIncludesVat,
      category_id: formData.category_id,
      stock_qty: parseInt(formData.stock_qty),
      low_stock_threshold: parseInt(formData.low_stock_threshold),
      is_addon: isAddon,
      addon_for_products: isAddon ? addonForProducts : [],
      addon_for_categories: isAddon ? addonForCategories : []
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success('Product updated');
        setIsModalOpen(false);
      } else {
        await axios.post(`${API}/products`, data);
        toast.success(`${data.name} added`);
        if (keepOpen) {
          setFormData(prev => ({
            name: '',
            price: '',
            category_id: prev.category_id,
            stock_qty: '100',
            low_stock_threshold: '10'
          }));
          setPriceIncludesVat(false);
          setIsAddon(false);
          setAddonForProducts([]);
          setAddonForCategories([]);
        } else {
          setIsModalOpen(false);
        }
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const toggleArchive = async (e, product) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/products/${product.id}`, { archived: !product.archived });
      toast.success(product.archived ? 'Product restored' : 'Product archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleExport = () => {
    window.open(`${API}/products/export/csv`, '_blank');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    
    // Check for duplicates
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/products/import/check`, formData);
      setImportPreview(response.data);
    } catch (error) {
      toast.error('Failed to parse CSV file');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(
        `${API}/products/import/csv?overwrite=${overwriteDuplicates}`,
        formData
      );
      
      toast.success(
        `Import complete: ${response.data.created} created, ${response.data.updated} updated, ${response.data.skipped} skipped`
      );
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      
      setIsImportModalOpen(false);
      setImportFile(null);
      setImportPreview(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to import products');
    }
  };

  return (
    <div data-testid="products-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Products</h1>
          <p className="text-slate-500 mt-1 font-body">Manage your product catalog</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="export-products-btn" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" data-testid="import-products-btn" onClick={() => setIsImportModalOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Button data-testid="add-product-btn" onClick={() => openModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="search-products"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40" data-testid="category-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label htmlFor="show-archived" className="text-sm text-slate-600">
            Show archived
          </Label>
        </div>
      </div>

      {/* Products grouped by category */}
      <div className="space-y-6">
        {sortedCategories.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-400">
            No products found
          </div>
        ) : (
          sortedCategories.map((catName) => (
            <div key={catName} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                <h3 className="font-heading font-semibold text-slate-700">{catName}</h3>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-8"></th>
                    <th>Product</th>
                    <th>Price (ex VAT)</th>
                    <th>Price (inc VAT)</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th className="text-right w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedProducts[catName].map((product) => (
                    <tr 
                      key={product.id} 
                      data-testid={`product-row-${product.id}`}
                      onClick={() => openModal(product)}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-product-${product.id}`}
                          onClick={() => openModal(product)}
                          className="p-1"
                        >
                          <Pencil className="w-4 h-4 text-slate-400" />
                        </Button>
                      </td>
                      <td className="font-medium text-slate-900">
                        {product.name}
                        {product.is_addon && (
                          <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Add-on</span>
                        )}
                      </td>
                      <td className="price-display">£{product.price.toFixed(2)}</td>
                      <td className="price-display">£{product.price_inc_vat.toFixed(2)}</td>
                      <td>
                        <span className={product.stock_qty <= product.low_stock_threshold ? 'text-amber-600 font-medium' : ''}>
                          {product.stock_qty}
                        </span>
                      </td>
                      <td>
                        {product.archived ? (
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">Archived</span>
                        ) : product.stock_qty <= product.low_stock_threshold ? (
                          <span className="low-stock-badge">Low Stock</span>
                        ) : (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Active</span>
                        )}
                      </td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`archive-product-${product.id}`}
                          onClick={(e) => toggleArchive(e, product)}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      {/* Product Edit/Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent data-testid="product-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editingProduct ? 'Edit product details' : 'Add a new product'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Product Name</Label>
              <Input
                id="name"
                data-testid="product-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price (£)</Label>
                <Input
                  id="price"
                  data-testid="product-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
                <div className="flex items-center gap-2 mt-1">
                  <Switch
                    id="price-vat"
                    checked={priceIncludesVat}
                    onCheckedChange={setPriceIncludesVat}
                  />
                  <Label htmlFor="price-vat" className="text-xs text-slate-500">
                    Price includes VAT
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, category_id: value });
                    const cat = categories.find(c => c.id === value);
                    if (cat && cat.name === 'Add-ons') setIsAddon(true);
                  }}
                >
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="stock">Stock Quantity</Label>
                <Input
                  id="stock"
                  data-testid="product-stock-input"
                  type="number"
                  min="0"
                  value={formData.stock_qty}
                  onChange={(e) => setFormData({ ...formData, stock_qty: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="threshold">Low Stock Threshold</Label>
                <Input
                  id="threshold"
                  data-testid="product-threshold-input"
                  type="number"
                  min="0"
                  value={formData.low_stock_threshold}
                  onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Add-on Configuration */}
            <div className="space-y-3 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">This is an Add-on</Label>
                  <p className="text-xs text-slate-500">Shows as a sub-option for other products on the till</p>
                </div>
                <Switch data-testid="product-is-addon" checked={isAddon} onCheckedChange={setIsAddon} />
              </div>

              {isAddon && (
                <div className="space-y-3 pt-2 border-t border-slate-200">
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Available for Products</Label>
                    <div className="max-h-28 overflow-y-auto space-y-1 bg-white rounded border p-2">
                      {allProducts.filter(p => !p.is_addon && !p.archived && p.id !== editingProduct?.id).map(p => (
                        <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                          <input type="checkbox" className="rounded border-slate-300"
                            checked={addonForProducts.includes(p.id)}
                            onChange={(e) => {
                              if (e.target.checked) setAddonForProducts(prev => [...prev, p.id]);
                              else setAddonForProducts(prev => prev.filter(id => id !== p.id));
                            }} />
                          <span className="text-slate-700">{p.name}</span>
                          <span className="text-xs text-slate-400 ml-auto">{getCategoryName(p.category_id)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Available for Categories</Label>
                    <div className="max-h-24 overflow-y-auto space-y-1 bg-white rounded border p-2">
                      {categories.map(cat => (
                        <label key={cat.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                          <input type="checkbox" className="rounded border-slate-300"
                            checked={addonForCategories.includes(cat.id)}
                            onChange={(e) => {
                              if (e.target.checked) setAddonForCategories(prev => [...prev, cat.id]);
                              else setAddonForCategories(prev => prev.filter(id => id !== cat.id));
                            }} />
                          <span className="text-slate-700">{cat.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              {!editingProduct && (
                <Button type="button" variant="secondary" data-testid="save-and-new-product-btn"
                  onClick={(e) => handleSubmit(e, true)}>
                  Add &amp; New
                </Button>
              )}
              <Button type="submit" data-testid="save-product-btn">
                {editingProduct ? 'Save Changes' : 'Add Product'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent data-testid="import-modal" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Import Products</DialogTitle>
            <DialogDescription>
              Upload a CSV file with columns: Name, Price (ex VAT), Category, Stock, Low Stock Threshold
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600">
                  {importFile ? importFile.name : 'Click to select CSV file'}
                </p>
              </label>
            </div>

            {importPreview && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm"><strong>Total items:</strong> {importPreview.total}</p>
                <p className="text-sm"><strong>New items:</strong> {importPreview.new_items.length}</p>
                <p className="text-sm text-amber-600">
                  <strong>Duplicates:</strong> {importPreview.duplicates.length}
                </p>
                
                {importPreview.duplicates.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="overwrite"
                        checked={overwriteDuplicates}
                        onCheckedChange={setOverwriteDuplicates}
                      />
                      <Label htmlFor="overwrite" className="text-sm">
                        Overwrite existing products
                      </Label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Duplicates: {importPreview.duplicates.slice(0, 5).join(', ')}
                      {importPreview.duplicates.length > 5 && ` and ${importPreview.duplicates.length - 5} more...`}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setIsImportModalOpen(false);
                setImportFile(null);
                setImportPreview(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleImport}
              disabled={!importFile || !importPreview}
              data-testid="confirm-import-btn"
            >
              Import {importPreview?.total || 0} Products
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
